import React, { useEffect, useState, useRef } from "react";
import type { SingleSiteEntry } from "../types";
import type { Habit } from "../../habits/types";
import Container from "common/Container";
import Header from "components/Header";
import Footer from "components/Footer";
import LargeCheck from "components/LargeCheck";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { getNextHabit } from "habits/selectors";
import { getHabitUrl } from "habits/util";
import { State } from "store";

export type Props = {
  log: SingleSiteEntry[] | null | undefined;
  now: number;
  time: number;
  habitId: string;
  habit: Habit;
  addLog: (arg0: SingleSiteEntry) => void;
};

function SiteWebView(props: {
  src: string;
  onComplete: (data?: string | null | undefined) => void;
  style: any;
  extras: string[];
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { onComplete, src, extras, style } = props;
  const [isReady, setIsReady] = useState(false);
  useEffect(() => {
    window.onmessage = function (e) {
      if (typeof e.data === "string") {
        if (e.data === src + " - complete") {
          onComplete();
        } else if (e.data.startsWith(src + ";complete;")) {
          // extract the data string
          const completionData = e.data.split(";complete;")[1];
          onComplete(completionData);
        } else if (e.data.startsWith(src + ";ready")) {
          setIsReady(true);
        } else if (e.data.contains(";")) {
          console.error(
            "Expected " +
              src +
              "; as the prefix for the message but found " +
              e.data +
              "\nShould the URLs match?"
          );
        }
      }
    };
  }, [onComplete, setIsReady, src]);
  useEffect(() => {
    if (isReady) {
      // pass the data to the child window
      const message = src + ";data;" + extras.join(";");
      const frame = iframeRef.current;

      if (frame instanceof HTMLIFrameElement) {
        frame.contentWindow.postMessage(message, "*");
      }
    }
  }, [isReady, extras, src]);
  return <iframe title="site" ref={iframeRef} style={style} src={src}></iframe>;
}

export default function SingleSite(props: Props) {
  const [isComplete, setIsComplete] = useState(false);
  useEffect(() => {
    setIsComplete(false);
  }, [props.habitId]);

  const nav = useNavigate();
  const nextHabit = useSelector<State, Habit | null>((state) =>
    getNextHabit(state, null)
  );

  const onClick = () => {
    //redirect to the next habit?
    if (nextHabit != null && nextHabit.destination != null) {
      nav(getHabitUrl(nextHabit.destination, nextHabit.habitId));
    } else {
      nav("/");
    }
  };

  const onComplete = (data?: string | null | undefined) => {
    const info =
      data == null
        ? {}
        : {
            data,
          };
    const logEntry: SingleSiteEntry = {
      type: props.habitId,
      time: props.time,
      info,
    };
    props.addLog(logEntry);
    setIsComplete(true);
  };

  const extras = [];

  if (props.log != null) {
    props.log.forEach((entry: SingleSiteEntry) => {
      if (entry.info.data != null) {
        extras.push(entry.info.data);
      }
    });
  }

  const title = props.habit.title;

  if (isComplete) {
    return (
      <Container>
        <Header title={title} />
        <LargeCheck />
        <Footer onClick={onClick} enabled={true} />
      </Container>
    );
  } else {
    return (
      <Container>
        <Header title={title} />
        <SiteWebView
          style={{
            height: "100%",
          }}
          onComplete={onComplete}
          src={props.habit.extras.url}
          extras={extras}
        />
      </Container>
    );
  }
}
